/**
 * Quiz Script API 클라이언트 (quiz/quiz_script 테이블 기반)
 */
import { apiClient } from './client'
import type {
  QuizScript,
  QuizSolveRequest,
  SolveResult,
} from '@quebon/shared-learning'

export type ContentQuizLocation =
  | 'pre'
  | 'post'
  | 'selected'
  | 'review'
  | 'block'
  | 'applied'
  | 'explanation'
  | 'checkup'
  | 'wrapup'
  | 'aiq'

/**
 * vars를 Base64 인코딩하여 헤더로 전달
 */
function encodeVarsToBase64(vars: Record<string, unknown>): string {
  return btoa(JSON.stringify(vars))
}

/**
 * Quiz Script API 클라이언트 구현
 * Note: quizId는 JS Number.MAX_SAFE_INTEGER를 초과할 수 있으므로 string 사용
 */
export const quizScriptApi = {
  getContentQuizzes: async (
    contentId: number,
    location?: ContentQuizLocation,
    showAnswer = false,
    useFixedVars = true,
    includeSolveResult = false,
  ): Promise<QuizScript[]> => {
    const response = await apiClient.get(`/quiz/contents/${contentId}/quizzes`, {
      params: { location, showAnswer, useFixedVars, includeSolveResult },
    })
    return response.data.data
  },

  /**
   * 퀴즈 조회 (quiz_id로)
   */
  getQuiz: async (
    quizId: string,
    showAnswer = false,
    vars?: Record<string, unknown>,
    useFixedVars?: boolean,
    includeSolveResult = false
  ): Promise<QuizScript> => {
    const headers: Record<string, string> = {}
    if (vars) {
      headers['x-qb-quiz-vars-base64'] = encodeVarsToBase64(vars)
    }

    const response = await apiClient.get(`/quiz/quizzes/${quizId}`, {
      params: { showAnswer, useFixedVars, includeSolveResult },
      headers,
    })
    return response.data.data
  },

  /**
   * 퀴즈 목록 조회
   */
  getQuizzes: async (
    ids: string[],
    showAnswer = false,
    useFixedVars?: boolean,
    includeSolveResult = false
  ): Promise<QuizScript[]> => {
    const response = await apiClient.get('/quiz/quizzes', {
      params: { ids: ids.join(','), showAnswer, useFixedVars, includeSolveResult },
    })
    return response.data.data
  },

  /**
   * 퀴즈 풀이
   */
  solve: async (
    quizId: string,
    request: QuizSolveRequest
  ): Promise<SolveResult> => {
    const response = await apiClient.post(
      `/quiz/quizzes/${quizId}/solve`,
      request
    )
    return response.data.data
  },

}

export default quizScriptApi
